import {Grid, Skeleton, TextField as MUITextField} from "@mui/material";
import {useEffect, useRef, useState} from "react";


import MuiAutocomplete from "@mui/material/Autocomplete";
import match from "autosuggest-highlight/match";
import parse from "autosuggest-highlight/parse";
import {AutocompleteProps, AutocompleteRenderInputParams, fieldToAutocomplete, fieldToTextField,} from "formik-mui";
import {useSnackbar} from "notistack";
import type {AeronetType} from "./types";
import {TypeService} from "./TypeService";

export interface TypeAutocompleteProps<
  T,
  Multiple extends boolean | undefined,
  DisableClearable extends boolean | undefined,
  FreeSolo extends boolean | undefined
> extends AutocompleteProps<T, Multiple, DisableClearable, FreeSolo> {
  parent_id: number;
  label?: string;
}

export function TypeAutocomplete<
  // eslint-disable-next-line
  T,
  Multiple extends boolean | undefined,
  DisableClearable extends boolean | undefined,
  FreeSolo extends boolean | undefined
>(
  props: TypeAutocompleteProps<AeronetType, Multiple, DisableClearable, FreeSolo>
) {
  const {
    parent_id,
    label,
    ...autocompleteProps
  } = props;
  const [types, setTypes] = useState<AeronetType[]>();
  let isRendered = useRef(false);
  const { enqueueSnackbar } = useSnackbar();
  const { name } = fieldToTextField(props as any);

  useEffect(() => {
    isRendered.current = true;

    // Guard the fetch on a usable parent. Without this, a caller whose parent id
    // comes from a config lookup that returned undefined (the prop is typed
    // `number`, so TypeScript never catches it) sends NO parent_id at all — and
    // GET /v6/types treats an absent parent_id as "no filter" and returns EVERY
    // active type, filling the dropdown with unrelated values. A parent of 0
    // (used by callers that mean "not chosen yet") queries parent_id=0 and
    // returns nothing, so skip that too.
    // Set [] rather than leaving `types` undefined: undefined renders the
    // Skeleton below forever, which would be a worse regression than an empty
    // control. Measured on module_g2_demo 2026-08-29.
    if (typeof parent_id !== "number" || !Number.isFinite(parent_id) || parent_id <= 0) {
      setTypes([]);
      return () => {
        isRendered.current = false;
      };
    }

    TypeService.query(parent_id)
      .then((resp) => {
        if (isRendered.current) {
          // Sort by priority first, then by name
          const sortedTypes = resp.sort((a, b) => {
            // Compare priorities
            const priorityA = a.priority ?? Number.MAX_SAFE_INTEGER;
            const priorityB = b.priority ?? Number.MAX_SAFE_INTEGER;
            
            if (priorityA !== priorityB) {
              return priorityA - priorityB;
            }
            
            // If priorities are equal, sort by name alphabetically
            const nameA = (a.name || '').toLowerCase();
            const nameB = (b.name || '').toLowerCase();
            return nameA.localeCompare(nameB);
          });
          setTypes(sortedTypes);
        }
      })
      .catch(() => {
        enqueueSnackbar("Error fetching types", {
          variant: "error",
        });
      });

    return () => {
      isRendered.current = false;
    };
  }, [enqueueSnackbar, parent_id]);

  return (
    <>
      {types ? (
        <MuiAutocomplete
          {...fieldToAutocomplete(autocompleteProps)}
          fullWidth
          autoComplete
          includeInputInList
          limitTags={3}
          options={types}
          disableCloseOnSelect={!!props.multiple}
          getOptionLabel={(option) => {
            if (typeof option === "string") {
              return option;
            } else {
              if (option.name) {
                return option.name;
              } else {
                const type = types.find((d) => option.id === d.id);
                return type?.name || "";
              }
            }
          }}
          isOptionEqualToValue={(
            option: AeronetType,
            value: AeronetType
          ) => {
            try {
              if (option.id === value.id) {
                if (!Object.prototype.hasOwnProperty.call(value, "name")) {
                  value.name = option.name;
                }
                return true;
              }
              // @ts-ignore
              if (value === "") {
                return true;
              }
              return false;
            } catch (e) {
              return false;
            }
          }}
          renderInput={(params: AutocompleteRenderInputParams) => (
            <MUITextField
              {...params}
              label={label ?? "Type"}
              fullWidth
              inputProps={{
                ...params.inputProps,
              }}
              error={props.form.touched[name!] && !!props.form.errors[name!]}
              // @ts-ignore
              helperText={props.form.touched[name!] && props.form.errors[name!]}
              variant="outlined"
            />
          )}
          renderOption={(props, option: AeronetType, state) => {
            const matches = match(option.name, state.inputValue);
            const types = parse(option.name, matches);

            return (
              <li {...props}>
                <Grid container alignItems="center">
                  
                  <Grid item xs>
                    {types.map((type, index) => (
                      <span
                        key={index}
                        style={{
                          fontWeight: type.highlight ? 700 : 400,
                        }}
                      >
                        {type.text}
                      </span>
                    ))}
                  </Grid>
                </Grid>
              </li>
            );
          }}
        />
      ) : (
        <Skeleton animation="wave" sx={{ fontSize: "2rem" }} />
      )}
    </>
  );
}

TypeAutocomplete.displayName = "FormikMaterialUITypeAutocomplete";
